import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { TimerIcon, ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { GrConfigure } from 'react-icons/gr';
import './NormalizerNode.css'; // Import the CSS file
import NormalizerConfigModal from './NormalizerConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const NormalizerNode = ({ id, data, selected }) => {
  const { interval, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addNormalizerNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`NormalizerNode ${id} received data:`, data);
  }, [id, data]);

  // Check if node is configured properly
  // Either explicitly marked as configured or has both interval AND a name
  const isConfigured = data.isConfigured || (!!name && !!interval);
  
  // Check if the node needs reconfiguration
  const needsReconfiguration = data.isApiConfigured === false || data.isConfigured === false;
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`NormalizerNode ${id} state:`, {
      isConfigured,
      needsReconfiguration,
      name,
      interval,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, needsReconfiguration, name, interval, data]);

  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  


  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      const target = data.interval === '15 mins' ? 15 : 60;
      
      addNormalizerNode(
        name,
        target,
        inputNodeName,
        { x: position.x, y: position.y }
      );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    // Get the source node ID
    const sourceNodeId = inputEdge.source;
    
    // Find the actual source node in the nodes array
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    if (!sourceNode) return sourceNodeId;
    
    // If the source node has a name in its data, use that
    if (sourceNode.data && sourceNode.data.name) {
      console.log(`Using source node name from data: ${sourceNode.data.name}`);
      return sourceNode.data.name;
    }
    
    // Get the node mapping
    const nodeMapping = getNodeMapping();
    
    // Get the actual node name from the mapping, or fall back to the ID
    const actualNodeName = nodeMapping[sourceNodeId] || sourceNodeId;
    
    console.log(`NormalizerNode ${id} input connection:`, {
      sourceNodeId,
      sourceNodeData: sourceNode.data,
      nodeMapping,
      actualNodeName
    });
    
    return actualNodeName;
  }, [edges, id, nodes, getNodeMapping]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      interval,
      isModalOpen
    });
    
    if (!hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // This allows editing an existing node even if the input is temporarily disconnected
      notifyError(new Error("Please connect an input to this normalizer node before configuring it."), {
        toastId: 'normalizer.connectInput',
      });
      return;
    }
    
    // Open the full configuration modal
    console.log(`Opening modal for ${needsReconfiguration ? 'reconfiguration' : isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, interval, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    console.log("Closing modal");
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, interval } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Convert interval to the target value expected by the API
      // Handle both string formats ('15 mins', '1 hr') and numeric formats ('15', '60')
      const target = interval === '15 mins' || interval === '15' ? 15 : 60;
      
      console.log("Converting interval to target:", {
        interval,
        target,
        intervalType: typeof interval
      });
      
      // Log the configuration data for debugging
      console.log(`Configuring normalizer node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
      // Add the normalizer node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Find the source node in the nodes array to get its actual name
        const inputEdge = edges.find(edge => edge.target === id);
        const sourceNodeId = inputEdge ? inputEdge.source : null;
        const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
        
        // Determine the actual input node name to use
        let actualInputNodeName = inputNodeName;
        
        // If the source node has a name in its data, use that
        if (sourceNode && sourceNode.data && sourceNode.data.name) {
          actualInputNodeName = sourceNode.data.name;
          console.log(`Using source node name from data: ${actualInputNodeName}`);
        }
        
        // Check if this is a reconfiguration (node already exists but input changed)
        const isReconfiguration = needsReconfiguration || (data.name && data.name === name);
        
        console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} normalizer node to session:`, {
          name,
          interval,
          inputNodeName: actualInputNodeName,
          position,
          isReconfiguration
        });
        
        // If this is a reconfiguration of the input node (not just editing parameters),
        // use the reconfigure API
        if (isReconfiguration && session.pipeline_name && 
            data.inputNodeName !== actualInputNodeName) {
          console.log(`Calling reconfigure API for node ${name} with new source ${actualInputNodeName}`);
          
          try {
            console.log(`Reconfiguring node ${name} with new input:`, {
              oldInput: data.inputNodeName,
              newInput: actualInputNodeName
            });
            
            // Call the API to reconfigure the node
            await reconfigureNode(
              session.pipeline_name,
              name,
              actualInputNodeName
            );
            
            console.log(`Node ${name} reconfigured successfully with new input ${actualInputNodeName}`);
          } catch (reconfigError) {
            console.error('Error reconfiguring node:', reconfigError);
            // Continue with local updates even if API call fails
          }
        } else if (isReconfiguration) {
          console.log(`Skipping reconfigure API call for node ${name} - only parameters were changed, not connections`);
        }
        
        // Now add/update the node in the session with the correct name
        addNormalizerNode(
          name,
          target,
          actualInputNodeName,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  interval,
                  isApiConfigured: true,
                  isConfigured: true,
                  inputNodeName: actualInputNodeName
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  interval,
                  isConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring normalizer node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editNormalizer });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addNormalizerNode, session, updateSelectedNode, edges, nodes, data, needsReconfiguration]);

  return (
    <>
      <div
        className={`node ${selected ? 'selected' : needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured'}`}
      >
        <div className={`node-header ${needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured'}`}>
          <div className="node-header-title">
            <div className="node-type">Normalizer</div>
            {name && (isConfigured || needsReconfiguration) && <div className="node-name-display">{name}</div>}
          </div>
          <div className="node-header-icons">
            {needsReconfiguration ? (
              <button 
                onClick={handleReconfigureInput}
                className="config-button"
                title="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="config-button"
                title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Normalizer" : "Connect an input first"}
                disabled={!hasInputConnection || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="icon" />
                ) : (
                  <ExclamationTriangleIcon className="icon" />
                )}
              </button>
            )}
            <TimerIcon className="icon" />
          </div>
        </div>
        <div className="node-content">
          <div className="text-xs">
            {!hasInputConnection && (
              <div className="text-warning">
                Connect an input to configure
              </div>
            )}
            {hasInputConnection && !interval && (
              <div className="text-warning">
                Time interval not selected
              </div>
            )}
            {isConfiguring && (
              <div className="text-info">
                Configuring...
              </div>
            )}
            <div className="normalizer-param-row">
              <span>Normalize to:</span>
              <span>{interval || 'not set'}</span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`connection-point ${needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured'}`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`connection-point ${needsReconfiguration ? 'needs-reconfiguration-output' : isConfigured ? 'configured-output' : 'not-configured'}`}
              isConnectable={isConfigured || needsReconfiguration} // Allow connections if the node is configured or needs reconfiguration
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <NormalizerConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={data.name && (needsReconfiguration || data.isApiConfigured === false)}
        initialData={{ 
          name, 
          interval,
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
          isConfigured: isConfigured
        }}
      />
    </>
  );
};

export default NormalizerNode;
